import {
  AfterViewChecked,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NotificationContext,
  NotificationContextHelper,
} from 'src/app/helper/notification-context.helper';

import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { HttpClient } from '@angular/common/http';
import { HttpService } from '../services/http.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { URLUtils } from '../urlUtils';
import { XmppService } from '../services/xmpp.service';
import { ensureActiveSubscription } from '../shared/subscription-dialog';
import { environment } from 'src/environments/environment';
import { ConfirmPlanComponent } from 'src/app/confirm-plan/confirm-plan.component';
import { MatDialog } from '@angular/material/dialog';

declare let Strophe: any;
declare let $msg: any;
declare let $pres: any;
declare let $iq: any;
declare let $: any;

// ======================================================
// INTERFACES
// ======================================================

interface Contact {
  id: string;
  guid?: string;
  name: string;
  initials: string;
  selval: string;
  type: string;
  groupName?: string;
  lastMessage?: string;
  lastMessageTime?: Date | string;
  lastMessageTimeDisplay?: string;
  unreadCount?: number;
  isOnline?: boolean;
  rel_id?: string;
  clientType?: string;
  source?: string;
  can_chat?: boolean;
}

interface Message {
  text: string;
  action: 'SENT' | 'RECEIVE';
  timestamp: any;
  date: string;
  id?: string;
  status?: 'sent' | 'delivered' | 'read';
}

@Component({
    selector: 'messages',
    templateUrl: 'messages.component.html',
    styleUrls: ['messages.component.scss'],
    standalone: false
})
export class MessagesComponent implements OnInit, OnDestroy, AfterViewChecked {
  // ======================================================
  // VIEW REFERENCES
  // ======================================================

  @ViewChild('scrollMe') myScrollContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChildren('contactItem') contactItems!: QueryList<ElementRef>;

  // ======================================================
  // UI STATE
  // ======================================================

  selectedValue = 'clients';
  selectedContact: Contact | null = null;
  messages: Message[] = [];
  messegeInput = '';
  clientSearch = '';
  teamSearch = '';
  chatSearchQuery = '';
  isSoloCategory = false;
  navigationSource = 'relationship';
  isContactTyping = false;
  isLoadingMore = false;

  // ======================================================
  // CONTACT LISTS
  // ======================================================

  flattenedClients: Contact[] = [];
  flattenedTeams: Contact[] = [];
  usersList: any[] = [];
  groups: any[] = [];

  // ======================================================
  // XMPP / CONNECTION
  // ======================================================

  domain = environment.xmppDomain;
  USERNAME = '';
  PASSWORD: any;
  URL = `wss://${this.domain}:5443/ws`;
  toJID = '';
  to_user_id: string | undefined;
  chatUserName = '';
  currentUserGuid = '';
  currentUserName = '';
  product = environment.product;
  firmName: any;
  isAdmin = true;
  data: any;
  userRole = 'AAM';

  // ======================================================
  // PRIVATE STATE
  // ======================================================

  private shouldScrollToBottom = false;
  private isConnectionEstablished = false;
  private client_type: string | undefined;
  private pendingNotificationGuid: string | null = null;
  private pendingNotificationId: string | null = null;
  private searchToastShown = false;
  private searchTimeout: any;
  private boundScrollHandler = this.onMessagesScroll.bind(this);

  // Notification
  notificationGuid: string | null = null;
  openedFromNotification = false;
  catsolo: any;
  isadmin: any;

  // Message dedup
  processedMsgIds = new Set();

  // Typing indicator
  private typingTimeout: any;
  private lastTypingSent = 0;
  private TYPING_THROTTLE = 3000;

  // RxJS subscriptions — cleaned up in ngOnDestroy
  private xmppSubs: Subscription[] = [];

  // MAM pagination
  private oldestMessageId: string | null = null;
  private hasMoreMessages = true;
  private PAGE_SIZE = 20;
  mamMessagesBuffer: any[] = [];
  private loadLatestDebounce: any;

  // Unread counts
  private UNREAD_KEY = 'unreadCounts';
  conversationMetaMap: Record<string, any> = {};

  // Contact load flags — used to defer fetchUnreadCounts until both lists ready
  private clientsLoaded = false;
  private teamsLoaded = false;
    
  getPlan:any;
  getCategory:any;
  restrictedPlans = ['free', 'professional', 'starter'];

  // ======================================================
  // CONSTRUCTOR
  // ======================================================

  constructor(
    private httpservice: HttpService,
    private router: Router,
    private toastr: ToastrService,
    private http: HttpClient,
    private spinnerService: NgxSpinnerService,
    private confirmationDialogService: ConfirmationDialogService,
    private xmppService: XmppService,
    private ngZone: NgZone,private dialog: MatDialog,
  ) {
    this.data = this.router.getCurrentNavigation()?.extras.state;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects.includes('/messages/teams')) {
          this.selectedValue = 'teams';
        } else if (event.urlAfterRedirects.includes('/messages/clients')) {
          this.selectedValue = 'clients';
        }
      }
    });
  }

  // ======================================================
  // LIFECYCLE HOOKS
  // ======================================================

  ngOnInit() {
    this.initUserContext();
    this.initXmppConnection();
    this.loadContacts();

    this.getPlan = localStorage.getItem("plan"); // get current plan
    this.getCategory = localStorage.getItem("category"); // get current category
  }

  ngAfterViewInit() {}

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.xmppSubs.forEach((s) => s.unsubscribe());
    this.xmppSubs = [];
    clearTimeout(this.loadLatestDebounce);
    clearTimeout(this.typingTimeout);
    this.isContactTyping = false;

    if (this.myScrollContainer?.nativeElement) {
      this.myScrollContainer.nativeElement.removeEventListener(
        'scroll',
        this.boundScrollHandler,
      );
    }
  }

  // ======================================================
  // INIT HELPERS
  // ======================================================

  private initUserContext(): void {
    this.catsolo = localStorage.getItem('category');
    this.isadmin = localStorage.getItem('isadmin');

    const state = history.state;
    if (state?.guid) {
      this.pendingNotificationGuid = state.guid;
      this.navigationSource = state.source;
    }
    else if (state?.clientId) {
    this.pendingNotificationId = state.clientId;   // new field
    this.navigationSource = state.source;
   }

    const ctx = NotificationContextHelper.consume();
    if (ctx) {
      this.pendingNotificationGuid = ctx.guid;
    }

    if (!this.pendingNotificationGuid && this.data?.guid) {
      this.pendingNotificationGuid = this.data.guid;
    }

    this.userRole = localStorage.getItem('role') || 'AAM';
    this.firmName = URLUtils.get_firmName();
    this.isAdmin = !URLUtils.get_userid() || URLUtils.get_userid() === 'admin';
    this.PASSWORD = localStorage.getItem('TOKEN');
    this.currentUserGuid = localStorage.getItem('userId') || '';
    this.currentUserName = localStorage.getItem('name') || '';
    this.USERNAME = `${URLUtils.get_jid()}@${this.domain}`;

    const category = localStorage.getItem('category');
    this.isSoloCategory = category === 'solo';

    if (this.userRole === 'AAM') {
      this.setDefaultTab();
    }
  }

  private initXmppConnection(): void {
    Strophe.ui = this;
    this.xmppService.connect(); // no-op if already connected
    Strophe.ui.conn = this.xmppService.conn;

    // If already connected when component mounts — probe immediately
    if (this.xmppService.isConnected) {
      this.isConnectionEstablished = true;
      this.fetchRosterOnConnect(); // ← re-check roster for new contacts
    }

    this.xmppSubs.push(
      this.xmppService.connectionStatus$.subscribe((status) => {
        Strophe.ui.conn = this.xmppService.conn;

        if (status === 'connected') {
          const wasAlreadyConnected = this.isConnectionEstablished;
          this.isConnectionEstablished = true;

          if (!wasAlreadyConnected) {
            this.toastr.success('Connection established successfully');
            this.fetchRosterOnConnect();
            // Fresh connection — ejabberd sends presence burst automatically
            // No probe needed here
          }
        } else if (status === 'disconnected' || status === 'error') {
          this.isConnectionEstablished = false;
        }
      }),
    );

    this.xmppSubs.push(
      this.xmppService.incomingPresence$.subscribe((presence: any) => {
        this.ngZone.run(() => this.onPresence(presence));
      }),
    );

    this.xmppSubs.push(
      this.xmppService.incomingMessage$.subscribe((msg: any) => {
        this.ngZone.run(() => this.stanzaHandler(msg));
      }),
    );
  }

  private loadContacts(): void {
    this.getChatUserList();
    this.getTeams();
  }

  private setDefaultTab(): void {
    this.selectedValue = this.userRole === 'AAM' ? 'teams' : 'clients';
    this.router.navigate([`/messages/${this.selectedValue}`]);
  }

  // ======================================================
  // PRESENCE HANDLING
  // ======================================================

  onPresence(presence: any): boolean {
    const from = $(presence).attr('from') || '';
    const type = $(presence).attr('type') || 'available';

    // Auto-accept incoming subscription requests
    if (type === 'subscribe') {
      const bareJid = from.split('/')[0];

      // Accept their subscription
      Strophe.ui.conn.send($pres({ to: bareJid, type: 'subscribed' }));

      // Subscribe back only if not already done
      if (!this.xmppService.subscribedJids.has(bareJid)) {
        Strophe.ui.conn.send($pres({ to: bareJid, type: 'subscribe' }));
        this.xmppService.subscribedJids.add(bareJid); // ← prevent duplicate subscribe
      }
      return true;
    }

    if (!from) return true;

    // Ignore all other subscription handshake types
    const subscriptionTypes = ['subscribed', 'unsubscribe', 'unsubscribed'];
    if (subscriptionTypes.includes(type)) return true;

    const bareJid = from.split('/')[0].toLowerCase();
    const guid = bareJid.split('@')[0];

    // Ignore own presence echo
    const myGuid = URLUtils.get_jid()?.toLowerCase() || '';
    if (guid === myGuid) return true;

    const isOnline = (this.xmppService.presenceMap[guid]?.size || 0) > 0;

    // ── Apply to contact lists ──
    const applyToList = (list: Contact[]) => {
      const contact = list.find((c) => c.guid?.toLowerCase() === guid);
      if (contact) contact.isOnline = isOnline;
    };

    applyToList(this.flattenedClients);
    applyToList(this.flattenedTeams);

    // Update selected contact header
    if (this.selectedContact?.guid?.toLowerCase() === guid) {
      this.selectedContact = {
        ...(this.selectedContact as Contact),
        isOnline,
      };
    }

    // Trigger Angular change detection
    this.flattenedClients = [...this.flattenedClients];
    this.flattenedTeams = [...this.flattenedTeams];

    return true;
  }

  private subscribeToUnsubscribedContacts(): void {
    const allContacts = [...this.flattenedClients, ...this.flattenedTeams];

    allContacts.forEach((contact) => {
      if (!contact.guid) return;
      const jid = `${contact.guid}@${this.domain}`;

      if (!this.xmppService.subscribedJids.has(jid)) {
        this.sendSubscribe(jid);
      }
    });
  }

  private sendSubscribe(jid: string): void {
    const presence = $pres({ to: jid, type: 'subscribe' });
    Strophe.ui.conn.send(presence);
  }

  private fetchRosterOnConnect(): void {
    if (this.xmppService.rosterFetched) return; // ← use service flag
    this.xmppService.rosterFetched = true; // ← set on service

    const rosterIQ = $iq({ type: 'get' }).c('query', {
      xmlns: 'jabber:iq:roster',
    });

    Strophe.ui.conn.sendIQ(rosterIQ, (roster: any) => {
      $(roster)
        .find('item')
        .each((_: any, item: any) => {
          const jid = $(item).attr('jid');
          const sub = $(item).attr('subscription');
          const ask = $(item).attr('ask');

          if (
            sub === 'both' ||
            sub === 'to' ||
            sub === 'from' ||
            ask === 'subscribe'
          ) {
            this.xmppService.subscribedJids.add(jid);
          }
        });

      this.subscribeToUnsubscribedContacts();
    });
  }

  getContactPresenceLabel(contact: Contact): string {
    if (contact.isOnline) return 'Online';

    const meta = this.conversationMetaMap[`${contact.guid}@${this.domain}`];
    if (!meta?.last_seen) return '';

    return `Last seen ${this.formatLastMessageTime(meta.last_seen)}`;
  }

  private applyPersistedPresence(): void {
    const apply = (list: Contact[]) => {
      list.forEach((contact) => {
        if (!contact.guid) return;
        const guid = contact.guid.toLowerCase();
        const mapEntry = this.xmppService.presenceMap[guid];
        contact.isOnline = mapEntry ? mapEntry.size > 0 : false;
      });
    };

    apply(this.flattenedClients);
    apply(this.flattenedTeams);

    // Trigger Angular change detection
    this.flattenedClients = [...this.flattenedClients];
    this.flattenedTeams = [...this.flattenedTeams];
  }

  // ======================================================
  // STANZA HANDLER — routes incoming XMPP stanzas
  // ======================================================

  stanzaHandler(message: any) {
    // Drop error stanzas immediately
    if ($(message).attr('type') === 'error') return true;

    // MAM history result — buffer for restoreMessages()
    const mamResult = $(message).find('result');
    if (mamResult.length) {
      this.mamMessagesBuffer.push(message);
      return true;
    }

    // XEP-0184 — delivery receipt
    const received = $(message).find('received[xmlns="urn:xmpp:receipts"]');
    if (received.length) {
      this.handleDeliveryReceipt(received.attr('id'));
      return true;
    }

    // XEP-0333 — read marker
    const displayed = $(message).find(
      'displayed[xmlns="urn:xmpp:chat-markers:0"]',
    );
    if (displayed.length) {
      this.handleReadMarker(displayed.attr('id'));
      return true;
    }

    // XEP-0085 — typing indicators
    const composing = $(message).find(
      'composing[xmlns="http://jabber.org/protocol/chatstates"]',
    );
    const paused = $(message).find(
      'paused[xmlns="http://jabber.org/protocol/chatstates"]',
    );
    const gone = $(message).find(
      'gone[xmlns="http://jabber.org/protocol/chatstates"]',
    );

    if (composing.length) {
      const from = $(message).attr('from')?.split('/')[0]?.split('@')[0];
      const myJid = URLUtils.get_jid()?.toLowerCase();

      // Ignore composing bounced back from own error stanza
      if (from === myJid) return true;

      if (from === this.selectedContact?.guid) {
        this.isContactTyping = true;
        this.shouldScrollToBottom = true;
      }
      return true;
    }

    if (paused.length || gone.length) {
      this.isContactTyping = false;
      return true;
    }

    // Live chat message
    this.xmlParser(message);
    return true;
  }

  // ======================================================
  // XEP-0085 — TYPING INDICATORS (outgoing)
  // ======================================================

  onInputChange(): void {
    if (!this.selectedContact || !this.canSendMessage()) return;

    // Reset debounce on every keystroke — pause fires 1s after LAST keystroke
    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      this.sendTypingState('paused');
      this.lastTypingSent = 0; // reset so next composing fires immediately
    }, 1000);

    // Throttle composing — max once every TYPING_THROTTLE ms
    const now = Date.now();
    if (now - this.lastTypingSent > this.TYPING_THROTTLE) {
      this.sendTypingState('composing');
      this.lastTypingSent = now;
    }
  }

  private sendTypingState(state: 'composing' | 'paused' | 'gone'): void {
    if (!this.selectedContact || !this.canSendMessage()) return;

    const stanza = $msg({
      to: `${this.toJID}@${this.domain}`,
      type: 'chat',
    }).c(state, { xmlns: 'http://jabber.org/protocol/chatstates' });

    Strophe.ui.conn.send(stanza);
  }

  // ======================================================
  // XEP-0184 — DELIVERY RECEIPTS
  // ======================================================

  private sendDeliveryReceipt(messageId: string, toJid: string): void {
    if (!messageId || !toJid || !this.canSendMessage()) return;

    const receipt = $msg({ to: toJid, type: 'chat' }).c('received', {
      xmlns: 'urn:xmpp:receipts',
      id: messageId,
    });

    Strophe.ui.conn.send(receipt);
  }

  private handleDeliveryReceipt(messageId: string): void {
    if (!messageId) return;

    const msg = this.messages.find(
      (m) => m.action === 'SENT' && m.id === messageId,
    );
    if (msg && msg.status !== 'read') {
      msg.status = 'delivered';
    }
  }

  // ======================================================
  // XEP-0333 — READ MARKERS
  // ======================================================

  private sendDisplayedMarker(messageId: string, toJid: string): void {
    if (!messageId || !toJid || !this.canSendMessage()) return;

    const marker = $msg({ to: toJid, type: 'chat' }).c('displayed', {
      xmlns: 'urn:xmpp:chat-markers:0',
      id: messageId,
    });

    Strophe.ui.conn.send(marker);
  }

  private handleReadMarker(messageId: string): void {
    if (!messageId) return;

    const idx = this.messages.findIndex(
      (m) => m.action === 'SENT' && m.id === messageId,
    );
    if (idx === -1) return;

    // Mark all sent messages up to and including this one as read
    for (let i = 0; i <= idx; i++) {
      if (this.messages[i].action === 'SENT') {
        this.messages[i].status = 'read';
      }
    }
  }

  // ======================================================
  // MESSAGE STATUS — apply server meta after MAM load
  // ======================================================

  private applyMessageStatusFromServer(contactGuid: string): void {
    const meta = this.conversationMetaMap[`${contactGuid}@${this.domain}`];
    if (!meta) return;

    const lastReadId = meta.last_read_message_id;
    const lastDeliveredId = meta.last_delivered_message_id;

    const findLastIdx = (id: string) => {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].action === 'SENT' && this.messages[i].id === id)
          return i;
      }
      return -1;
    };

    const lastReadIdx = lastReadId ? findLastIdx(lastReadId) : -1;
    const lastDeliveredIdx = lastDeliveredId
      ? findLastIdx(lastDeliveredId)
      : -1;

    this.messages.forEach((msg, idx) => {
      if (msg.action !== 'SENT') return;

      if (lastReadIdx !== -1 && idx <= lastReadIdx) {
        msg.status = 'read';
      } else if (lastDeliveredIdx !== -1 && idx <= lastDeliveredIdx) {
        msg.status = 'delivered';
      } else {
        msg.status = 'sent';
      }
    });
  }

  // ======================================================
  // API CALLS
  // ======================================================

  getChatUserList() {
    const url = `${URLUtils.getChatUserList}`;

    this.httpservice.getFeaturesdata(url).subscribe((res: any) => {
      const appointmentClients: any[] = res?.relationships || [];

      appointmentClients.forEach((item: any) => {
        item.clientType =
          (item.type?.toLowerCase() === 'entity' || item.type?.toLowerCase() === 'corporate') ? 'Entity' : 'Consumer';
        item.can_chat = item.can_chat === true;

        const existingIndex = this.usersList.findIndex((u) => u.id === item.id);
        if (existingIndex === -1) {
          this.usersList.push(item);
        } else {
          if (item.can_chat === false) {
            this.usersList[existingIndex].can_chat = false;
          }
        }
      });

      this.flattenClientsData();
    });
  }

  loadEntityMembers(entity: any) {
    this.httpservice
      .sendGetRequest(URLUtils.getNotify(entity))
      .subscribe((res: any) => {
        entity.members = entity?.data?.users || [];
        this.flattenClientsData();
      });
  }

  private fetchUnreadCountsFromServer(): void {
    const teamJids =
      this.flattenedTeams?.map((t: any) => `${t.guid}@${this.domain}`) || [];
    const clientJids =
      this.flattenedClients?.map((c: any) => `${c.guid}@${this.domain}`) || [];
    const to_jids = [...teamJids, ...clientJids];

    if (!to_jids.length) return;

    const data = { user_jid: this.USERNAME, chat_jids: to_jids };

    this.httpservice
      .sendPostChatRequest(URLUtils.getChatMetaDetails, data)
      .subscribe((response: any) => {
        const metaList = response?.data;
        if (!Array.isArray(metaList)) return;

        metaList.forEach((meta: any) => {
          const contactGuid = meta.chat_jid?.split('@')[0];
          if (!contactGuid) return;

          this.conversationMetaMap[meta.chat_jid] = meta;
          this.applyMetaToContact(contactGuid, meta);
        });

        this.flattenedClients = this.sortContacts(this.flattenedClients);
        this.flattenedTeams = this.sortContacts(this.flattenedTeams);
      });
  }

  private checkAndFetchUnreadCounts(): void {
    if (this.clientsLoaded && this.teamsLoaded) {
      this.fetchUnreadCountsFromServer();
    }
  }

  private applyMetaToContact(guid: string, meta: any): void {
    const applyTo = (list: Contact[]) => {
      const contact = list.find((c) => c.guid === guid);
      if (!contact) return;

      contact.unreadCount = meta.unread_count || 0;

      const serverTime = meta.last_message_timestamp
        ? new Date(meta.last_message_timestamp).getTime()
        : 0;
      const localTime = contact.lastMessageTime
        ? new Date(contact.lastMessageTime).getTime()
        : 0;

      if (serverTime >= localTime) {
        contact.lastMessage =
          meta.last_message_preview || contact.lastMessage || '';
        contact.lastMessageTime = meta.last_message_timestamp
          ? new Date(meta.last_message_timestamp)
          : contact.lastMessageTime;
        contact.lastMessageTimeDisplay = meta.last_message_timestamp
          ? this.formatLastMessageTime(meta.last_message_timestamp)
          : contact.lastMessageTimeDisplay || '';
      }
    };

    applyTo(this.flattenedClients);
    applyTo(this.flattenedTeams);
  }

  getTeams() {
    const params = this.userRole === 'GH' ? { module: 'matter' } : undefined;

    this.httpservice
      .getFeaturesdata(URLUtils.getMembers, params)
      .subscribe((res: any) => {
        const users = res?.data?.users || [];

        this.flattenedTeams = users
          .filter((u: any) => u.guid && u.guid !== URLUtils.get_jid())
          .map((u: any) => ({
            id: u.id,
            guid: u.guid,
            name: u.name,
            initials: this.getInitials(u.name),
            type: 'individual',
            selval: 'teams',
            isOnline: false,
            unreadCount: this.getUnreadCount(u.guid),
            lastMessage: '',
            lastMessageTime: null,
            lastMessageTimeDisplay: '',
          }));

        this.flattenedTeams = this.sortContacts(this.flattenedTeams);
        this.applyPersistedPresence();

        this.teamsLoaded = true;
        this.checkAndFetchUnreadCounts();
        this.tryOpenChatFromNotification();

        if (this.isConnectionEstablished) {
          this.subscribeToUnsubscribedContacts();
        }
      });
  }

  // ======================================================
  // FLATTEN CONTACT DATA
  // ======================================================

  flattenClientsData() {
    const map = new Map<string, Contact>();

    this.usersList.forEach((item) => {
      if (item.clientType?.toLowerCase() !== 'consumer') {
        // Entity — flatten members
        (item.members || []).forEach((m: any) => {
          if (!m?.guid || !m?.name) return;
          map.set(m.guid, {
            id: m.id,
            guid: m.guid,
            name: m.name,
            initials: this.getInitials(m.name),
            type: 'entity',
            groupName: item.name,
            selval: 'clients',
            unreadCount: this.getUnreadCount(m.guid),
            isOnline: false,
            source: item.source || 'relationship',
            can_chat: item.can_chat !== false,
          });
        });
      } 
      
      else if (item.clientType?.toLowerCase() === 'consumer' && item.guid) {
        // Consumer — direct contact
        map.set(item.guid, {
          id: item.id,
          guid: item.guid,
          name: item.name,
          selval: 'clients',
          initials: this.getInitials(item.name),
          type: 'consumer',
          unreadCount: this.getUnreadCount(item.guid),
          isOnline: false,
          source: item.source || 'relationship',
          can_chat: item.can_chat === true,
        });
      }
    });

    this.flattenedClients = this.sortContacts(Array.from(map.values()));
    this.applyPersistedPresence();
    this.clientsLoaded = true;
    this.checkAndFetchUnreadCounts();
    this.tryOpenChatFromNotification();
  }

  getCurrentSource(contact: Contact): string {
    if (
      this.navigationSource &&
      this.pendingNotificationGuid === contact.guid
    ) {
      return this.navigationSource;
    }
    return contact.source || 'relationship';
  }

  // ======================================================
  // CHAT — SELECT CONTACT & SEND MESSAGE
  // ======================================================

  selectChatUser(contact: Contact) {
    // Clear typing state from previous chat
    this.isContactTyping = false;

    // Notify previous contact we've left
    if (this.selectedContact && this.selectedContact.guid !== contact.guid) {
      this.sendTypingState('gone');
    }

    if (this.selectedContact?.guid !== contact.guid) {
      this.processedMsgIds.clear();
    }

    this.searchToastShown = false;
    this.chatSearchQuery = '';
    this.selectedContact = contact;
    this.toJID = contact.guid || '';
    this.to_user_id = contact.id;
    this.client_type = contact.type;
    this.messages = [];
    this.oldestMessageId = null;
    this.hasMoreMessages = true;

    contact.unreadCount = 0;
    this.clearUnreadCount(contact.guid!);
    contact.source = this.getCurrentSource(contact);

    if (this.xmppService.isConnected) {
      this.restoreMessages(undefined, false);
    }
    this.shouldScrollToBottom = true;
    this.focusMessageInput(false);
    this.scrollToSelectedContact();

    setTimeout(() => {
      const el = this.myScrollContainer?.nativeElement;
      if (!el) return;
      el.removeEventListener('scroll', this.boundScrollHandler);
      el.addEventListener('scroll', this.boundScrollHandler);
    }, 0);
  }

  //subscription check for action button
  checkPlan(event?: MouseEvent): boolean {
    const restrictedPlans = ['free', 'professional', 'starter'];

    if (restrictedPlans.includes(this.getPlan)) {
      this.dialog.open(ConfirmPlanComponent, {
        width: '400px',
        data: { plan: this.getPlan }
      });

      return false;   // Stop further execution
    }
    return true;      // Allow further execution
  }

  sendMessage(message: string) {
    if (!ensureActiveSubscription(this.confirmationDialogService)) return;
        
    if (!this.checkPlan()) {
      return;
    } // Stop here if plan is restricted

    if (!message.trim() || !this.selectedContact || !this.toJID) return;

    if (this.isChatDisabled()) {
      this.toastr.error('Chat is not available for this appointment.');
      return;
    }

    if (!this.canSendMessage()) {
      this.toastr.error('Chat connection not ready. Please wait.');
      return;
    }

    const now = new Date();
    const messageId = Strophe.ui.conn.getUniqueId('msg');

    this.messages.push({
      text: message,
      action: 'SENT',
      timestamp: now,
      date: now.toISOString().split('T')[0],
      id: messageId,
      status: 'sent',
    });
    this.shouldScrollToBottom = true;
    this.updateContactPreview(this.selectedContact.guid!, message, now);
    this.messegeInput = '';

    const subject =
      localStorage.getItem('name') +
      ' ##' +
      URLUtils.get_jid() +
      '## #N#' +
      localStorage.getItem('name') +
      '#N#';

    const msgXML = $msg({
      to: `${this.toJID}@${this.domain}`,
      type: 'chat',
      id: messageId,
    })
      .c('subject')
      .t(subject)
      .up()
      .c('body')
      .t(message)
      .up()
      .c('request', { xmlns: 'urn:xmpp:receipts' }); // XEP-0184 receipt request

    Strophe.ui.conn.send(msgXML);

    // Push notification
    const from_user_id = localStorage.getItem('user_id');
    if (this.selectedValue === 'teams') this.client_type = 'entity';

    const jid = localStorage.getItem('jid');
    const guid = jid + '_' + from_user_id;

    this.httpservice
      .sendPostNotyRequest(URLUtils.Notification, {
        from_user_id,
        to_user_id: this.to_user_id,
        client_type: this.client_type,
        message: 'New Message',
        navigation_data: {
          route_name: 'message_client_inbox',
          params: { guid },
        },
      })
      .subscribe();

    this.focusMessageInput(false);
  }

  // ======================================================
  // MAM — MESSAGE ARCHIVE (pagination + initial load)
  // ======================================================

  onMessagesScroll() {
    const container = this.myScrollContainer?.nativeElement;
    if (!container) return;

    if (
      container.scrollTop <= this.PAGE_SIZE &&
      this.hasMoreMessages &&
      !this.isLoadingMore &&
      this.oldestMessageId
    ) {
      this.restoreMessages(this.oldestMessageId, true);
    }
  }

  restoreMessages(beforeId?: string, appendOldMessages: boolean = false) {
    if (!this.selectedContact || this.isLoadingMore) return;
    if (!this.xmppService.conn || !this.xmppService.isConnected) {
      console.warn('restoreMessages called before connection ready');
      return;
    }

    this.isLoadingMore = true;
    this.spinnerService.show('paginationLoader');
    this.mamMessagesBuffer = [];

    const query = $iq({ type: 'set' })
      .c('query', { xmlns: 'urn:xmpp:mam:2' })
      .c('x', { xmlns: 'jabber:x:data', type: 'submit' })
      .c('field', { var: 'FORM_TYPE', type: 'hidden' })
      .c('value')
      .t('urn:xmpp:mam:2')
      .up()
      .up()
      .c('field', { var: 'with' })
      .c('value')
      .t(`${this.selectedContact.guid}@${this.domain}`)
      .up()
      .up()
      .up()
      .c('set', { xmlns: 'http://jabber.org/protocol/rsm' })
      .c('max')
      .t(this.PAGE_SIZE.toString())
      .up();

    if (beforeId) {
      query.c('before').t(beforeId).up();
    } else {
      query.c('before').up();
    }

    const loadedForGuid = this.selectedContact.guid;

    Strophe.ui.conn.sendIQ(
      query,
      (response: any) => {
        if (this.selectedContact?.guid !== loadedForGuid) {
          this.isLoadingMore = false;
          this.mamMessagesBuffer = [];
          this.spinnerService.hide('paginationLoader');
          return;
        }

        const results = this.mamMessagesBuffer;

        if (results.length === 0) {
          this.hasMoreMessages = false;
          this.isLoadingMore = false;
          this.mamMessagesBuffer = [];
          this.spinnerService.hide('paginationLoader');
          return;
        }

        const parsedMessages: Message[] = [];
        let firstMessageId: string | null = null;

        results.forEach((messageStanza: any, index: number) => {
          const result = $(messageStanza).find('result');
          const resultId = result.attr('id');
          if (index === 0) firstMessageId = resultId;

          const forwarded = result.find('forwarded');
          const message = forwarded.find('message');
          const bodyEl = message.find('body');
          if (!bodyEl.length) return;

          const body = Strophe.xmlunescape(Strophe.getText(bodyEl[0]));
          const from = message.attr('from') || '';
          const msgId = message.attr('id') || '';
          const delayStamp = forwarded.find('delay').attr('stamp');
          const timestamp = delayStamp ? new Date(delayStamp) : new Date();

          const normalizeJid = (jid: string) =>
            jid?.split('/')[0]?.toLowerCase();
          const action: 'SENT' | 'RECEIVE' =
            normalizeJid(from) === normalizeJid(this.USERNAME)
              ? 'SENT'
              : 'RECEIVE';

          parsedMessages.push({
            text: body,
            action,
            timestamp,
            date: new Date(timestamp).toISOString().split('T')[0],
            id: msgId,
            status: action === 'SENT' ? 'sent' : undefined,
          });
        });

        this.oldestMessageId = firstMessageId;
        parsedMessages.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );

        if (appendOldMessages) {
          const container = this.myScrollContainer?.nativeElement;
          const oldHeight = container?.scrollHeight ?? 0;
          this.messages = [...parsedMessages, ...this.messages];

          setTimeout(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - oldHeight;
            }
            this.isLoadingMore = false;
            this.mamMessagesBuffer = [];
            this.spinnerService.hide('paginationLoader');
          });
        } else {
          this.messages = parsedMessages;
          this.mamMessagesBuffer = [];

          // Update contact preview from last MAM message
          if (parsedMessages.length > 0) {
            const last = parsedMessages[parsedMessages.length - 1];
            const contact = this.findContact(loadedForGuid!);
            if (contact) {
              const serverTime = new Date(last.timestamp).getTime();
              const localTime = contact.lastMessageTime
                ? new Date(contact.lastMessageTime).getTime()
                : 0;
              if (serverTime >= localTime) {
                this.updateContactPreview(
                  loadedForGuid!,
                  last.text,
                  last.timestamp,
                );
              }
            }
          }

          setTimeout(() => {
            const container = this.myScrollContainer?.nativeElement;
            if (container) container.scrollTop = container.scrollHeight;

            this.isLoadingMore = false;
            this.spinnerService.hide('paginationLoader');

            this.applyMessageStatusFromServer(loadedForGuid!);

            // XEP-0333 — mark last message as displayed to reset unread on server
            const lastMsg = this.messages[this.messages.length - 1];
            if (lastMsg?.id && loadedForGuid) {
              this.sendDisplayedMarker(
                lastMsg.id,
                `${loadedForGuid}@${this.domain}`,
              );
            }
          });

          this.shouldScrollToBottom = true;
        }

        if (parsedMessages.length < this.PAGE_SIZE) {
          this.hasMoreMessages = false;
        }
      },
      (error: any) => {
        console.error('MAM ERROR => ', error);
        this.isLoadingMore = false;
        this.mamMessagesBuffer = [];
        this.spinnerService.hide('paginationLoader');
      },
    );
  }

  // ======================================================
  // XML PARSER — live incoming messages
  // ======================================================

  xmlParser(msg: any) {
    const id =
      $(msg).find('stanza-id').attr('id') ||
      $(msg).find('archived').attr('id') ||
      $(msg).find('result').attr('id') ||
      $(msg).attr('id');

    if (id && this.processedMsgIds.has(id)) return;
    if (id) this.processedMsgIds.add(id);

    let timeStamp: string | Date;
    let action: 'SENT' | 'RECEIVE' | 'NONE' = 'RECEIVE';
    let from = '';
    let to = '';
    let body = '';
    let stanzaId = $(msg).attr('id') || '';

    const forwarded = $(msg).find('forwarded')[0];

    if (forwarded) {
      const message = $(forwarded).find('message')[0];
      to = $(message).attr('to');
      from = $(message).attr('from');
      stanzaId = $(message).attr('id') || '';
      const delayStamp = $(forwarded).find('delay').attr('stamp');
      timeStamp = delayStamp ? new Date(delayStamp) : new Date();
    } else {
      to = $(msg).attr('to');
      from = $(msg).attr('from');
      stanzaId = $(msg).attr('id') || '';
      timeStamp = new Date();
    }

    const normalizeJid = (jid: string) => jid?.split('/')[0].toLowerCase();
    const bareFromJid = normalizeJid(from);
    const bareToJid = normalizeJid(to);
    const myJid = normalizeJid(this.USERNAME);

    if (bareToJid === myJid) action = 'RECEIVE';
    else if (bareFromJid === myJid) action = 'SENT';
    else action = 'NONE';

    if (action === 'NONE') return;

    const bodies = $(msg).find('body');
    body =
      bodies.length > 0 ? Strophe.xmlunescape(Strophe.getText(bodies[0])) : '';
    if (!body) return;

    const contactId =
      action === 'SENT' ? bareToJid.split('@')[0] : bareFromJid.split('@')[0];

    const isNewMessage = !forwarded;
    const alreadyAddedBySend =
      action === 'SENT' &&
      isNewMessage &&
      this.selectedContact?.guid === contactId;

    if (this.selectedContact?.guid === contactId && !alreadyAddedBySend) {
      this.isContactTyping = false; // clear typing bubble when real message arrives

      this.messages.push({
        text: body,
        action,
        timestamp: timeStamp,
        date: new Date(timeStamp).toISOString().split('T')[0],
        id: stanzaId,
        status: action === 'SENT' ? 'sent' : undefined,
      });
      this.messages.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
      this.shouldScrollToBottom = true;
    }

    // Update contact list preview
    const incomingTime = new Date(timeStamp).getTime();
    const existingContact = this.findContact(contactId);
    const existingTime = existingContact?.lastMessageTime
      ? new Date(existingContact.lastMessageTime).getTime()
      : 0;

    if (incomingTime >= existingTime) {
      this.updateContactPreview(contactId, body, timeStamp);
    }

    if (action === 'RECEIVE' && !forwarded) {
      this.sendDeliveryReceipt(stanzaId, bareFromJid); // XEP-0184

      const isChatOpen = this.selectedContact?.guid === contactId;

      if (isChatOpen) {
        this.sendDisplayedMarker(stanzaId, bareFromJid); // XEP-0333
      } else {
        // Increment unread badge and notify
        const contact = this.findContact(contactId);
        if (contact) {
          contact.unreadCount = (contact.unreadCount || 0) + 1;

          this.toastr.success(
            `New message from ${contact.name || 'User'}`,
            '',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
            },
          );

          this.flattenedClients = this.sortContacts(this.flattenedClients);
          this.flattenedTeams = this.sortContacts(this.flattenedTeams);
        }
      }
    }
  }

  // ======================================================
  // NOTIFICATION FLOW
  // ======================================================

  handleNotificationFlow(ctx: NotificationContext) {
    if (!ctx?.guid) return;
    this.tryOpenChatFromNotification();
    this.notificationGuid = ctx.guid;
    this.openedFromNotification = true;
  }

  // private tryOpenChatFromNotification(): void {
  //   if (!this.pendingNotificationGuid) return;

  //   const allContacts = [...this.flattenedClients, ...this.flattenedTeams];
  //   const match = allContacts.find(
  //     (c) => c.guid === this.pendingNotificationGuid,
  //   );
  //   if (!match) return;

  //   this.selectedValue = match.selval;
  //   this.router.navigate([`/messages/${match.selval}`], { replaceUrl: true });
  //   this.selectChatUser(match);
  //   this.pendingNotificationGuid = null;
  // }
  private tryOpenChatFromNotification(): void {
  if (!this.pendingNotificationGuid && !this.pendingNotificationId) return;

  const allContacts = [...this.flattenedClients, ...this.flattenedTeams];
  const match = allContacts.find(
    (c) =>
      (this.pendingNotificationGuid && c.guid === this.pendingNotificationGuid) ||
      (this.pendingNotificationId && c.id === this.pendingNotificationId),
  );
  if (!match) return;

  this.selectedValue = match.selval;
  this.router.navigate([`/messages/${match.selval}`], { replaceUrl: true });
  this.selectChatUser(match);
  this.pendingNotificationGuid = null;
  this.pendingNotificationId = null;
}

  // ======================================================
  // SORTING & CONTACT HELPERS
  // ======================================================

  sortContacts(list: Contact[]): Contact[] {
    return [...list].sort((a, b) => {
      const unreadDiff = (b.unreadCount || 0) - (a.unreadCount || 0);
      if (unreadDiff !== 0) return unreadDiff;

      const timeA = a.lastMessageTime
        ? new Date(a.lastMessageTime).getTime()
        : 0;
      const timeB = b.lastMessageTime
        ? new Date(b.lastMessageTime).getTime()
        : 0;
      return timeB - timeA;
    });
  }

  sortAllLists() {
    this.flattenedClients = this.sortContacts(this.flattenedClients);
    this.flattenedTeams = this.sortContacts(this.flattenedTeams);
  }

  private updateContactPreview(
    guid: string,
    text: string,
    timestamp: Date | string,
  ): void {
    const applyTo = (list: Contact[]) => {
      const contact = list.find((c) => c.guid === guid);
      if (!contact) return;
      contact.lastMessage = text;
      contact.lastMessageTime = timestamp;
      contact.lastMessageTimeDisplay = this.formatLastMessageTime(timestamp);
    };

    applyTo(this.flattenedClients);
    applyTo(this.flattenedTeams);

    this.flattenedClients = this.sortContacts(this.flattenedClients);
    this.flattenedTeams = this.sortContacts(this.flattenedTeams);
  }

  private findContact(guid: string): Contact | undefined {
    return (
      this.flattenedClients.find((c) => c.guid === guid) ||
      this.flattenedTeams.find((c) => c.guid === guid)
    );
  }

  trackByGuid(index: number, contact: Contact) {
    return contact.guid || contact.id;
  }

  private scrollToSelectedContact(): void {
    setTimeout(() => {
      if (!this.selectedContact || !this.contactItems) return;

      const index = this.getFilteredContacts().findIndex(
        (c) => c.id === this.selectedContact?.id,
      );
      const el = this.contactItems.toArray()[index]?.nativeElement;
      if (!el) return;

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  // ======================================================
  // UNREAD COUNT HELPERS (localStorage)
  // ======================================================

  private getUnreadMap(): Record<string, number> {
    const key = `${this.UNREAD_KEY}_${URLUtils.get_jid()}`;
    return JSON.parse(localStorage.getItem(key) || '{}');
  }

  private saveUnreadMap(map: Record<string, number>) {
    const key = `${this.UNREAD_KEY}_${URLUtils.get_jid()}`;
    localStorage.setItem(key, JSON.stringify(map));
  }

  private getUnreadCount(guid: string): number {
    return this.getUnreadMap()[guid] || 0;
  }

  private clearUnreadCount(guid: string): void {
    const map = this.getUnreadMap();
    if (map[guid]) {
      delete map[guid];
      this.saveUnreadMap(map);
    }
  }

  // ======================================================
  // SEARCH HELPERS
  // ======================================================

  getFilteredContacts(): Contact[] {
    const list =
      this.selectedValue === 'clients'
        ? this.flattenedClients
        : this.flattenedTeams;
    const term =
      this.selectedValue === 'clients' ? this.clientSearch : this.teamSearch;

    if (!term) return list;

    const searchTerm = term.toLowerCase();
    return list.filter(
      (c) =>
        c.name?.toLowerCase().includes(searchTerm) ||
        c.groupName?.toLowerCase().includes(searchTerm),
    );
  }

  getFilteredMessages(): Message[] {
    if (!this.chatSearchQuery.trim()) return this.messages;
    const term = this.chatSearchQuery.toLowerCase();
    return this.messages.filter((m) => m.text.toLowerCase().includes(term));
  }

  groupFilteredMessagesByDate() {
    return this.getFilteredMessages().reduce((acc: any, m) => {
      acc[m.date] = acc[m.date] || [];
      acc[m.date].push(m);
      return acc;
    }, {});
  }

  onChatSearchEnter() {
    this.searchToastShown = false;
    this.showNoMessageFoundToastIfNeeded();
  }

  onChatSearchChange() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      if (!this.chatSearchQuery.trim()) {
        this.searchToastShown = false;
        return;
      }
      this.showNoMessageFoundToastIfNeeded();
    }, 300);
  }

  private showNoMessageFoundToastIfNeeded() {
    const hasQuery = this.chatSearchQuery.trim().length > 0;
    const noResults = this.getFilteredMessages().length === 0;

    if (hasQuery && noResults && !this.searchToastShown) {
      this.toastr.info('No messages found', '', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      this.searchToastShown = true;
    }
  }

  // ======================================================
  // FORMAT HELPERS
  // ======================================================

  formatLastMessageTime(dateInput: Date | string): string {
    if (!dateInput) return '';

    const date = new Date(dateInput);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) =>
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear();

    if (sameDay(date, today)) {
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    if (sameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString();
  }

  formatDateStamp(dateString: string): string {
    const d = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    [today, yesterday, d].forEach((x) => x.setHours(0, 0, 0, 0));

    if (+d === +today) return 'Today';
    if (+d === +yesterday) return 'Yesterday';
    return d.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  formatTime(date: Date): string {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const m = minutes < 10 ? '0' + minutes : minutes;
    return `${h}:${m} ${ampm}`;
  }

  groupMessagesByDate() {
    return this.messages.reduce((acc: any, m) => {
      acc[m.date] = acc[m.date] || [];
      acc[m.date].push(m);
      return acc;
    }, {});
  }

  getObjectKeys(obj: any) {
    return Object.keys(obj);
  }

  // ======================================================
  // TICK STATUS — template helper for message receipts
  // ======================================================

  getMessageStatusIcon(msg: Message): string {
    if (msg.action !== 'SENT') return '';

    switch (msg.status) {
      case 'read':
        return 'double-tick-blue'; // ✓✓ blue
      case 'delivered':
        return 'double-tick-grey'; // ✓✓ grey
      case 'sent':
      default:
        return 'single-tick-grey'; // ✓  grey
    }
  }

  // ======================================================
  // UI HELPERS
  // ======================================================

  private canSendMessage(): boolean {
    return (
      Strophe?.ui?.conn &&
      Strophe.ui.conn.connected &&
      this.isConnectionEstablished
    );
  }

  scrollToBottom(focusInput: boolean = false): void {
    if (!this.myScrollContainer?.nativeElement) return;

    requestAnimationFrame(() => {
      const el = this.myScrollContainer.nativeElement;
      el.scrollTop = el.scrollHeight;

      if (focusInput) {
        requestAnimationFrame(() => this.focusMessageInput());
      }
    });
  }

  private focusMessageInput(scroll: boolean = true) {
    setTimeout(() => {
      this.messageInput?.nativeElement?.focus();
    }, 0);
  }

  getInitials(name: string): string {
    const firstWord = name.trim().split(' ')[0];
    return firstWord ? firstWord[0].toUpperCase() : '';
  }

  getAvatarColor(initials: string): string {
    const colors = ['avatar-red', 'avatar-purple', 'avatar-teal'];
    return colors[initials.charCodeAt(0) % colors.length];
  }

  StartVideo() {
    if(this.userRole === 'AAM'){
      this.isadmin = true;
    }
    // console.log('catsolo', this.catsolo);

    if (!ensureActiveSubscription(this.confirmationDialogService)) { return; }
    if (!this.checkPlan()) {
      return;
    } // Stop here if plan is restricted

    const link = `${environment.AVChat}?token=${localStorage.getItem('TOKEN')}
    &jid=${URLUtils.get_jid()}
    &name=${localStorage.getItem('name')}
    &plan=${localStorage.getItem('plan')}
    &hideclient=${this.isadmin}
    &category=${this.catsolo}`;
    // console.log(link);
    // console.log(JSON.stringify(link));
    // console.log('Video chat link:', link);
    // console.log(window.location.href);
    // console.log(window.location.search);

    window.open(link, '_blank');
  }

  isChatDisabled(): boolean {
    return this.selectedContact?.can_chat === false;
  }

  isMessageEmpty(): boolean {
    return !this.messegeInput.trim();
  }

  restricttextSpace(event: any) {
    event.target.value = event.target.value.replace(/^\s+/, '');
  }

  onPasteBlocked(event: ClipboardEvent) {
    event.preventDefault();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage(this.messegeInput);
    }
  }

  isActive(value: string) {
    this.selectedValue = value;
    this.clientSearch = '';
    this.teamSearch = '';
    this.chatSearchQuery = '';
    this.searchToastShown = false;
    this.router.navigate([`/messages/${value}`]);
  }

  isActiveTeam(value: string) {
    this.isActive(value);
  }
}